import { useCallback, useEffect, useRef } from 'react';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { loadDrafts } from '../drafts/draftsSlice';
import {
  useCancelAllDraftRemindersMutation,
  useCancelDraftReminderMutation,
  useSaveDraftReminderMutation,
} from './api';

/**
 * Mirrors local drafts to the server so an abandoned one can be reminded.
 *
 * Drafts live only in IndexedDB (§3.3) — the server has no idea a half-written
 * letter exists. This hook is the one bridge: after the writer pauses, it tells
 * the server "a draft exists for this conversation". The server resets the
 * reminder clock on every call, so active writing never triggers a nag; only a
 * draft left untouched past the delay does.
 *
 * When a draft disappears (sealed, discarded, or signed out) the pending
 * reminder is cancelled. Reminders are deliberately *not* cancelled just because
 * the tab closed — that is the whole point of scheduling them server-side.
 */

/** How long the writer must pause before the server is told about the draft. */
const SYNC_IDLE_MS = 15_000;

export function useDraftReminders(): { cancelAll: () => Promise<void> } {
  const dispatch = useAppDispatch();
  const drafts = useAppSelector((s) => s.drafts.byId);
  const [saveReminder] = useSaveDraftReminderMutation();
  const [cancelReminder] = useCancelDraftReminderMutation();
  const [cancelAllReminders] = useCancelAllDraftRemindersMutation();

  const loaded = useRef(false);
  useEffect(() => {
    if (loaded.current) return;
    loaded.current = true;
    void dispatch(loadDrafts());
  }, [dispatch]);

  // relationshipId -> the draft `updatedAt` we last told the server about.
  const synced = useRef(new Map<string, number>());
  const known = useRef(new Set<string>());

  useEffect(() => {
    const ids = new Set(Object.keys(drafts));

    // A draft that vanished was sealed or discarded — drop its reminder.
    for (const id of known.current) {
      if (!ids.has(id)) {
        synced.current.delete(id);
        void cancelReminder({ relationshipId: id }).unwrap().catch(() => undefined);
      }
    }
    known.current = ids;

    const timer = window.setTimeout(() => {
      for (const [id, draft] of Object.entries(drafts)) {
        if (!draft.body.trim()) {
          // Cleared to nothing: there is no draft to remind about.
          if (synced.current.has(id)) {
            synced.current.delete(id);
            void cancelReminder({ relationshipId: id }).unwrap().catch(() => undefined);
          }
          continue;
        }
        if (synced.current.get(id) === draft.updatedAt) continue;
        synced.current.set(id, draft.updatedAt);
        void saveReminder({ relationshipId: id })
          .unwrap()
          .catch(() => synced.current.delete(id)); // retry on the next pause
      }
    }, SYNC_IDLE_MS);

    return () => window.clearTimeout(timer);
  }, [drafts, saveReminder, cancelReminder]);

  const cancelAll = useCallback(async (): Promise<void> => {
    await cancelAllReminders().unwrap().catch(() => undefined);
  }, [cancelAllReminders]);

  return { cancelAll };
}
